import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, MessageSquare, FileText, ChevronRight, CheckCircle2, ShieldCheck, Zap, Camera, Search, Menu, ChevronLeft, ArrowUpRight, Check, Globe, Sparkles, Users, Receipt, Clock, Upload, Lock } from 'lucide-react';
import { LogoIcon, LogoFull } from '../components/brand/Logo';
import AICopilot from '../components/AICopilot';
import AnimatedHeroBackground, { HeroHud } from '../components/ui/AnimatedHeroBackground';
import axios from '../api/axios';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [societyName, setSocietyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isYearly, setIsYearly] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !phone || !societyName) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await axios.post("/auth/demo-request", { name, phone, societyName });
      setSuccess(true);
      setName("");
      setPhone("");
      setSocietyName("");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
        }
      });
    }, { threshold: 0.12 });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (e: React.MouseEvent<HTMLElement>) => {
    const btn = e.currentTarget;
    const a = btn.nextElementSibling as HTMLElement;
    const arrow = btn.querySelector('.faq-arrow') as HTMLElement;
    const isOpen = a.classList.contains('open');

    document.querySelectorAll('.faq-a').forEach(el => el.classList.remove('open'));
    document.querySelectorAll('.faq-arrow').forEach(el => el.classList.remove('open'));

    if (!isOpen) {
      a.classList.add('open');
      arrow.classList.add('open');
    }
  };

  return (
    <div className="landing-container font-sans text-text overflow-x-hidden selection:bg-primary/10">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .landing-container {
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .reveal {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .faq-a { max-height: 0; overflow: hidden; transition: max-height 0.3s ease, padding 0.3s; }
        .faq-a.open { max-height: 200px; padding-bottom: 20px; }
        .faq-arrow { transition: transform 0.2s; }
        .faq-arrow.open { transform: rotate(45deg); }

        .hero-gradient {
          background: radial-gradient(circle at 50% -20%, #DBEAFE 0%, transparent 40%),
                      radial-gradient(circle at 0% 100%, #F1F5F9 0%, transparent 30%);
        }

        .nav-scrolled {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid #E2E8F0;
        }

        @keyframes freeCardGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(79,124,255,0), 0 4px 16px -4px rgba(79,124,255,0.08); }
          50%       { box-shadow: 0 0 0 4px rgba(79,124,255,0.1), 0 12px 32px -8px rgba(79,124,255,0.22); }
        }
        .free-card-glow {
          animation: freeCardGlow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Fixed blueprint-city backdrop — persists behind every section */}
      <AnimatedHeroBackground />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[100] h-20 px-[5%] flex items-center justify-between bg-white/80 backdrop-blur-md border-b border-border">
        <Link to="/" className="flex items-center group">
          <LogoFull light={false} size="md" />
        </Link>
        <div className="hidden md:flex items-center gap-10">
          <a href="#onboarding" className="text-sm font-medium text-muted hover:text-primary transition-colors">How It Works</a>
          <a href="#pricing" className="text-sm font-medium text-muted hover:text-primary transition-colors">Pricing</a>
          <a href="#nocash-payments" className="text-sm font-medium text-muted hover:text-primary transition-colors">Payments</a>
          <a href="#ai-assistant" className="text-sm font-medium text-muted hover:text-primary transition-colors">AI Assistant</a>
          <a href="#features" className="text-sm font-medium text-muted hover:text-primary transition-colors">Features</a>
          <a href="#faq" className="text-sm font-medium text-muted hover:text-primary transition-colors">FAQ</a>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/auth" className="text-sm font-semibold text-text hover:text-primary transition-colors px-4 py-2">Login</Link>
          <a href="#contact" className="btn-primary">Book a Demo</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-40 pb-16 md:pt-52 md:pb-20 overflow-hidden">
        <HeroHud />
        <div className="max-w-7xl mx-auto px-[5%] relative z-[3] flex flex-col items-center text-center">

          <h1 className="reveal text-[clamp(40px,6vw,72px)] font-bold text-text leading-[1.05] tracking-tight max-w-[900px] mb-8" style={{ fontFamily: "'Sora', sans-serif", letterSpacing: "-0.03em" }}>
            Manage your society <br/>
            <span className="text-primary">from one platform</span>
          </h1>

          <p className="reveal text-lg md:text-xl text-muted max-w-[720px] leading-relaxed mb-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Billing, payments, resident management, WhatsApp communication, AI assistance, and reports — all in one secure platform. Go live in under 10 minutes. No credit card required.
          </p>

          <div className="reveal flex flex-wrap justify-center gap-3.5 mb-16">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
              style={{ padding: "15px 30px", borderRadius: 12, background: "linear-gradient(140deg,#4f7cff,#2f5fe0)", color: "#fff", fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 15.5, boxShadow: "0 18px 40px -16px rgba(59,110,246,0.75)" }}
            >
              Start Free <ArrowUpRight size={18} />
            </Link>
            <a
              href="#contact"
              className="inline-flex items-center transition-transform hover:-translate-y-0.5"
              style={{ padding: "15px 28px", borderRadius: 12, background: "#ffffff", border: "1px solid rgba(20,40,90,0.14)", color: "#0e1730", fontWeight: 600, fontSize: 15.5, boxShadow: "0 10px 28px -18px rgba(34,67,140,0.4)" }}
            >
              Book a Demo
            </a>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 border-t border-border pt-12 w-full max-w-4xl">
            {[
              { val: "90%", label: "Work automated" },
              { val: "10 min", label: "Setup time" },
              { val: "₹0", label: "Setup costs" },
              { val: "Secure", label: "Encrypted payments" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-text mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>{stat.val}</div>
                <div className="text-xs font-semibold text-muted ">{stat.label}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Trust Strip — the first thing after the hero, kept light and scannable */}
      <section className="relative z-10 pb-16 md:pb-24 px-[5%]">
        <div className="reveal max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-4 py-6 px-8 bg-white/70 backdrop-blur-sm border border-white/80 rounded-2xl shadow-[0_12px_32px_-20px_rgba(34,67,140,0.25)]">
          {[
            "We never store your debit or credit card details",
            "No credit card required to start",
            "Secure, encrypted payment processing",
            "Setup in under 10 minutes"
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm font-semibold text-text/80">
              <Check size={14} className="text-success shrink-0" strokeWidth={3} />
              {item}
            </div>
          ))}
        </div>
      </section>

      {/* Dashboard Showcase Section — shown early so visitors see the real product before reading feature copy */}
      <section id="dashboard" className="py-32 px-[5%] bg-white/50 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-sm font-bold text-primary mb-4">The Complete Picture</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-6" style={{ fontFamily: "'Sora', sans-serif" }}>
              Everything in one dashboard
            </h3>
            <p className="text-lg text-muted">One place for every number, every resident, every payment, and every report your society will ever need.</p>
          </div>

          {/* Module grid — 8 core modules */}
          <div className="reveal grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            {[
              { icon: <Sparkles size={18} />, label: "PD Orbit AI", highlight: true },
              { icon: <Zap size={18} />, label: "Smart Payments", highlight: true },
              { icon: <Receipt size={18} />, label: "Manage Funds" },
              { icon: <FileText size={18} />, label: "Auto-Billing" },
              { icon: <Users size={18} />, label: "Residents" },
              { icon: <Search size={18} />, label: "Reports" },
              { icon: <Camera size={18} />, label: "AI Bill Extract" },
              { icon: <Upload size={18} />, label: "Data Migration" }
            ].map((mod, i) => (
              <div key={i} className={`flex items-center gap-3 p-4 rounded-2xl backdrop-blur-sm border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(34,67,140,0.25)] ${mod.highlight ? 'bg-primary/5 border-primary/20' : 'bg-white/70 border-white/80'}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${mod.highlight ? 'bg-primary/15 text-primary' : 'bg-primary/10 text-primary'}`}>
                  {mod.icon}
                </div>
                <span className="text-[13px] font-bold text-text/80 leading-tight">{mod.label}</span>
              </div>
            ))}
          </div>

          {/* Dashboard screenshot */}
          <div className="reveal w-full rounded-3xl border border-border bg-white shadow-[0_32px_64px_-32px_rgba(34,67,140,0.35)] overflow-hidden relative group">
            <div className="bg-slate-50 border-b border-slate-100 px-5 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-slate-200" />
                <div className="w-3 h-3 rounded-full bg-slate-200" />
                <div className="w-3 h-3 rounded-full bg-slate-200" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 bg-white border border-slate-200 rounded-full text-[11px] text-slate-400 font-medium">
                  app.propertydirect.in/dashboard
                </div>
              </div>
            </div>
            <img
              src="/dashboard_mockup.png"
              alt="PropertyDirect Dashboard"
              className="w-full h-auto transform transition-transform duration-700 group-hover:scale-[1.01]"
            />
          </div>

          <div className="reveal mt-10 flex justify-center">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-semibold text-sm hover:-translate-y-0.5 transition-transform"
              style={{ background: "linear-gradient(140deg,#4f7cff,#2f5fe0)", boxShadow: "0 14px 32px -14px rgba(79,124,255,0.65)" }}
            >
              Start Free <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Onboarding Steps Section */}
      <section id="onboarding" className="py-32 px-[5%] bg-white/60 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-sm font-bold text-primary mb-4">Seamless Onboarding</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-6" style={{ fontFamily: "'Sora', sans-serif" }}>Get started in minutes</h3>
            <p className="text-lg text-muted">No IT team needed. No complex setup. Five steps to a fully running society portal.</p>
          </div>

          <div className="relative">
            {/* Connecting gradient line (desktop only) */}
            <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent pointer-events-none" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
              {[
                { num: "01", icon: <Globe size={22} />, title: "Create Your Society", desc: "Register with your property details in under 5 minutes." },
                { num: "02", icon: <Users size={22} />, title: "Import Blocks & Flats", desc: "Upload Excel or enter manually. AI validates data instantly." },
                { num: "03", icon: <FileText size={22} />, title: "Configure Billing", desc: "Set charges, water tariffs, and due dates in one screen." },
                { num: "04", icon: <MessageSquare size={22} />, title: "Invite Residents", desc: "They receive a WhatsApp link to their self-service portal." },
                { num: "05", icon: <CreditCard size={22} />, title: "Start Collecting", desc: "Send bills, receive payments, and auto-reconcile funds." }
              ].map((step, i) => (
                <div key={i} className="reveal flex flex-col items-center text-center group">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-2xl bg-white border border-primary/20 flex items-center justify-center text-primary shadow-[0_8px_24px_-12px_rgba(79,124,255,0.28)] group-hover:scale-105 group-hover:shadow-[0_14px_32px_-10px_rgba(79,124,255,0.45)] transition-all duration-300">
                      {step.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                      {step.num}
                    </div>
                  </div>
                  <h4 className="font-bold text-text mb-2 text-[15px]" style={{ fontFamily: "'Sora', sans-serif" }}>{step.title}</h4>
                  <p className="text-sm text-muted leading-relaxed px-2">{step.desc}</p>
                  {i < 4 && (
                    <div className="md:hidden mt-6 flex justify-center">
                      <ChevronRight size={16} className="text-primary/30 rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="reveal mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-semibold text-sm hover:-translate-y-0.5 transition-transform"
              style={{ background: "linear-gradient(140deg,#4f7cff,#2f5fe0)", boxShadow: "0 14px 32px -14px rgba(79,124,255,0.65)" }}
            >
              Start for Free <ArrowUpRight size={16} />
            </Link>
            <a href="#contact" className="text-sm font-semibold text-muted hover:text-primary transition-colors">
              Or book a guided demo →
            </a>
          </div>

          {/* Trust strip */}
          <div className="reveal mt-12 pt-10 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            {[
              "Setup in under 10 minutes",
              "No technical knowledge required",
              "Import your existing Excel data",
              "AI guides every step"
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm font-medium text-muted">
                <Check size={14} className="text-success shrink-0" />
                {item}
              </div>
            ))}
          </div>

          <div className="reveal mt-6 flex justify-center">
            <div className="inline-flex items-center gap-2.5 px-5 py-3 bg-primary/5 border border-primary/15 rounded-2xl">
              <Clock size={14} className="text-primary" />
              <span className="text-sm font-semibold text-text">Average setup time: <span className="text-primary">8–10 minutes</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-24 px-[5%] bg-white/55 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-sm font-bold text-primary mb-4">Trust & Security</h2>
            <h3 className="text-4xl font-bold text-text tracking-tight mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
              Your society's data stays protected
            </h3>
          </div>
          <div className="reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <ShieldCheck size={20} />, title: "Encrypted payment processing", desc: "Every payment runs through our secure, encrypted payment gateway." },
              { icon: <CreditCard size={20} />, title: "Card details never stored", desc: "PropertyDirect never stores your debit or credit card details on its servers." },
              { icon: <Lock size={20} />, title: "Role-based financial access", desc: "Only authorized admins and committee members can view sensitive financial data." },
              { icon: <Receipt size={20} />, title: "Automatic receipts", desc: "Every payment generates an instant receipt and a complete, auditable history." }
            ].map((item, i) => (
              <div key={i} className="flex flex-col gap-3 p-6 rounded-2xl bg-white/70 backdrop-blur-sm border border-white/80">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  {item.icon}
                </div>
                <h5 className="font-bold text-text text-[15px]">{item.title}</h5>
                <p className="text-sm text-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 px-[5%] bg-white/65 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center mb-14">
            <h2 className="text-sm font-bold text-primary mb-4">Transparent Pricing</h2>
            <h3 className="text-4xl font-bold text-text tracking-tight mb-3">Start free. Scale when you're ready.</h3>
            <p className="text-sm text-muted">Trusted by apartment communities and gated societies across India.</p>
          </div>

          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 items-stretch">

            {/* ── Free card — hidden on yearly, animated glow on monthly ── */}
            {!isYearly && <div className="relative flex flex-col p-7 rounded-2xl bg-white/80 backdrop-blur-sm border border-primary/25 free-card-glow transition-all duration-300 hover:-translate-y-1 lg:w-1/4 shrink-0">
              <div className="mb-5">
                <h4 className="text-lg font-bold text-text mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>Free</h4>
                <p className="text-[13px] text-muted mb-4">Get started, no credit card needed</p>
                <span className="text-5xl font-bold text-primary" style={{ fontFamily: "'Sora', sans-serif" }}>Free</span>
                <p className="text-[11px] text-success font-medium mt-1.5">No billing, ever.</p>
              </div>
              <div className="h-px w-full mb-5 bg-primary/10" />
              <ul className="space-y-3 mb-7 flex-1">
                {["Up to 5 flats", "Manual Billing", "Digital Receipts", "Basic Dashboard"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-text/90 font-medium">
                    <Check size={14} className="text-success shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="/auth" className="w-full py-3.5 rounded-xl text-center font-semibold text-sm bg-primary text-white hover:bg-primaryHover shadow-[0_10px_24px_-12px_rgba(59,110,246,0.45)] transition-all">
                Start for Free
              </a>
            </div>}

            {/* ── Vertical divider (desktop only, monthly only) ── */}
            {!isYearly && <div className="hidden lg:block w-px bg-slate-200 shrink-0 self-stretch my-3" />}

            {/* ── Paid plans: toggle lives here, above these 3 cards only ── */}
            <div className="flex-1 flex flex-col">
              <div className="flex justify-center lg:justify-end mb-7">
                <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1">
                  <button
                    onClick={() => setIsYearly(false)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${!isYearly ? 'bg-white text-text shadow-sm' : 'text-muted hover:text-text'}`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setIsYearly(true)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${isYearly ? 'bg-white text-text shadow-sm' : 'text-muted hover:text-text'}`}
                  >
                    Yearly
                    <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">2 months free</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 flex-1 overflow-visible">
                {[
                  { name: "Gold",     monthly: 399, yearly: 332, saving: 798,  desc: "Best for small apartments",  features: ["Up to 20 flats", "Payment Collection", "40 WhatsApp/month", "Digital Receipts"] },
                  { name: "Diamond",  monthly: 699, yearly: 582, saving: 1398, desc: "Most popular for societies", features: ["20–100 flats", "Full Automation Suite", "68 WhatsApp/month", "OCR Bill Scanning", "Priority Support"], featured: true },
                  { name: "Platinum", monthly: 999, yearly: 832, saving: 2004, desc: "Enterprise society scale",   features: ["100+ flats", "Custom Branding", "125 WhatsApp/month", "Dedicated Manager", "Unlimited Admins"] }
                ].map((plan) => {
                  const price = isYearly ? plan.yearly : plan.monthly;
                  return (
                    <div key={plan.name} className={`reveal relative flex flex-col rounded-2xl bg-white/80 backdrop-blur-sm border transition-all duration-300 hover:-translate-y-1
                      ${plan.featured
                        ? 'border-primary/50 ring-1 ring-primary/15 shadow-[0_28px_56px_-24px_rgba(59,110,246,0.55)] sm:scale-[1.03] sm:z-10 p-8'
                        : 'border-white/80 p-7 hover:shadow-[0_20px_44px_-26px_rgba(34,67,140,0.35)]'}`}>
                      {plan.featured && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-semibold px-3.5 py-1 rounded-full shadow-sm whitespace-nowrap">
                          Most Popular
                        </div>
                      )}
                      <div className="mb-5">
                        <h4 className="text-lg font-bold text-text mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>{plan.name}</h4>
                        <p className="text-[13px] text-muted mb-4">{plan.desc}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-bold text-text" style={{ fontFamily: "'Sora', sans-serif" }}>₹{price}</span>
                          <span className="text-muted text-sm font-medium">/mo</span>
                        </div>
                        {isYearly ? (
                          <p className="text-[11px] text-emerald-600 font-semibold mt-1.5">Save ₹{plan.saving.toLocaleString('en-IN')} · billed annually</p>
                        ) : (
                          <p className="text-[11px] text-slate-400 mt-1.5">or ₹{plan.yearly}/mo billed yearly</p>
                        )}
                      </div>
                      <div className="h-px w-full mb-5 bg-primary/10" />
                      <ul className="space-y-3 mb-7 flex-1">
                        {plan.features.map((f) => (
                          <li key={f} className="flex items-center gap-2.5 text-sm text-text/90 font-medium">
                            <Check size={14} className="text-success shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                      <a href="/auth" className={`w-full py-3.5 rounded-xl text-center font-semibold text-sm transition-all
                        ${plan.featured
                          ? 'bg-primary text-white hover:bg-primaryHover shadow-[0_14px_30px_-14px_rgba(59,110,246,0.7)]'
                          : 'bg-white/70 text-text hover:bg-white border border-border'}`}>
                        Start {plan.name}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="reveal flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-14">
            {["Cancel anytime", "No hidden fees", "Secure payments", "No auto debit", "Card details never stored"].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-muted font-medium">
                <Check size={14} className="text-success shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NoCash Payments Section */}
      <section id="nocash-payments" className="py-32 px-[5%] bg-white/60 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

          {/* Text side — left */}
          {/* TODO(business/payments): "funds go directly into your society's bank account, NoCash never holds funds" is a settlement-flow
              claim that can't be verified from application code alone (it depends on how the NoCash gateway is configured/contracted).
              Confirm accuracy with the payments team before production release. */}
          <div className="reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-[10px] font-bold mb-6">
              <Zap size={11} /> NoCash Payments
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-8" style={{ fontFamily: "'Sora', sans-serif" }}>
              Residents pay in one tap.<br />Money lands in your account.
            </h3>
            <p className="text-lg text-muted mb-10 leading-relaxed">
              NoCash is the digital payments layer built into every plan. Residents receive a WhatsApp payment link, pay via UPI, card, or net banking in under 30 seconds, and the funds go directly into your society's bank account — with zero platform commission.
            </p>

            {/* Key highlights grid */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {[
                { stat: "0%", label: "Platform commission" },
                { stat: "< 30s", label: "Resident payment time" },
                { stat: "3x", label: "Higher collection rate vs SMS" },
                { stat: "Auto", label: "Reconciliation & receipts" }
              ].map((item, i) => (
                <div key={i} className="bg-white/70 border border-white/80 rounded-2xl px-5 py-4 backdrop-blur-sm">
                  <div className="text-2xl font-bold text-primary mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>{item.stat}</div>
                  <div className="text-xs font-semibold text-muted">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="space-y-5">
              {[
                { title: "Direct to your bank", desc: "NoCash never holds your funds. Every payment settles directly into your society's registered account." },
                { title: "UPI, cards & net banking", desc: "Residents choose how they pay. All modes accepted, all receipts auto-generated and sent on WhatsApp." },
                { title: "Automatic reconciliation", desc: "Every payment is matched to the right flat and demand — no manual reconciliation, no missed entries." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-success/10 text-success flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <div>
                    <h5 className="font-bold text-text">{item.title}</h5>
                    <p className="text-sm text-muted">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment flow mockup — right */}
          <div className="reveal bg-text rounded-[32px] p-12 relative shadow-2xl">
            <div className="absolute inset-0 bg-dot-grid opacity-[0.1] pointer-events-none rounded-[32px]"></div>
            <div className="relative z-10 space-y-3">

              {/* WhatsApp notification */}
              <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <MessageSquare size={13} className="text-emerald-400" />
                  </div>
                  <span className="text-slate-400 text-[11px] font-medium">WhatsApp · NoCash Society</span>
                  <span className="ml-auto text-slate-600 text-[10px]">Just now</span>
                </div>
                <p className="text-slate-300 text-[13px] leading-relaxed">
                  Maintenance due for July 2026 — ₹4,200. Pay instantly:
                </p>
                <div className="mt-2.5 px-4 py-2 bg-primary/20 border border-primary/30 rounded-xl text-primary text-[12px] font-bold text-center">
                  Pay ₹4,200 →
                </div>
              </div>

              {/* Payment method */}
              <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4">
                <div className="text-slate-500 text-[10px] font-bold mb-3">Select payment method</div>
                <div className="space-y-2">
                  {[
                    { label: "UPI", sub: "PhonePe · GPay · Paytm", selected: true },
                    { label: "Credit / Debit Card", sub: "Visa · Mastercard · RuPay", selected: false },
                    { label: "Net Banking", sub: "All major banks", selected: false }
                  ].map((mode, i) => (
                    <div key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${mode.selected ? 'bg-primary/15 border-primary/30' : 'bg-white/3 border-white/8'}`}>
                      <div className={`w-3 h-3 rounded-full border-2 shrink-0 ${mode.selected ? 'border-primary bg-primary' : 'border-white/20'}`} />
                      <div>
                        <div className={`text-[12px] font-bold ${mode.selected ? 'text-white' : 'text-slate-400'}`}>{mode.label}</div>
                        <div className="text-[10px] text-slate-600">{mode.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Success */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl px-5 py-4 text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 size={20} className="text-emerald-400" />
                </div>
                <div className="text-white font-bold text-sm mb-1">Payment Successful</div>
                <div className="text-emerald-400 text-[12px] font-medium">₹4,200 · Flat B-204 · July 2026</div>
                <div className="text-slate-500 text-[11px] mt-1.5">Receipt sent on WhatsApp · Ledger updated</div>
              </div>
            </div>
          </div>
        </div>

        <div className="reveal mt-16 flex justify-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-semibold text-sm hover:-translate-y-0.5 transition-transform"
            style={{ background: "linear-gradient(140deg,#4f7cff,#2f5fe0)", boxShadow: "0 14px 32px -14px rgba(79,124,255,0.65)" }}
          >
            Book a Demo <ArrowUpRight size={16} />
          </a>
        </div>
      </section>

      {/* AI Assistant Showcase Section */}
      <section id="ai-assistant" className="py-32 px-[5%] bg-white/45 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-[10px] font-bold mb-6">
              <Sparkles size={12} /> Powered by PD Orbit
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>Meet PD Orbit</h3>
            <p className="text-lg font-semibold text-primary/80 mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>Your AI Copilot for PropertyDirect</p>
            <p className="text-lg text-muted mb-10">Ask anything about your society in plain language. PD Orbit understands billing, payments, residents, and finances — and acts on your behalf in real time.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {["Setup Assistance", "Billing Help", "Report Generation", "Data Migration", "Financial Queries", "Resident Management"].map((cap) => (
                <span key={cap} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/70 border border-primary/15 rounded-full text-[12px] font-semibold text-text/80">
                  <Check size={11} className="text-success shrink-0" strokeWidth={3} /> {cap}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Chat mockup 1 — billing query */}
            <div className="reveal bg-white/80 backdrop-blur-sm border border-white/80 rounded-3xl p-8 shadow-[0_20px_44px_-24px_rgba(34,67,140,0.22)] hover:-translate-y-1 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Sparkles size={14} />
                </div>
                <span className="text-sm font-bold text-text">PD Orbit</span>
                <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online
                </span>
              </div>

              <div className="flex justify-end mb-4">
                <div className="max-w-[80%] bg-primary text-white text-sm font-medium px-4 py-3 rounded-2xl rounded-tr-sm shadow-sm">
                  How do I generate water bills?
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-1">
                  <Sparkles size={12} />
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-text flex-1">
                  <p className="font-semibold mb-3">Here's how to generate water bills in 3 steps:</p>
                  <div className="space-y-2.5 mb-4">
                    {[
                      "Enter meter readings for each flat",
                      "Set consumption rates in Water Billing Rules",
                      "Click Generate Bills — done in seconds"
                    ].map((step, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                        <span className="text-[13px] text-muted">{step}</span>
                      </div>
                    ))}
                  </div>
                  <button className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primaryHover transition-colors">
                    Go to Water Billing <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* Chat mockup 2 — onboarding scenario */}
            <div className="reveal bg-white/80 backdrop-blur-sm border border-white/80 rounded-3xl p-8 shadow-[0_20px_44px_-24px_rgba(34,67,140,0.22)] hover:-translate-y-1 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Sparkles size={14} />
                </div>
                <span className="text-sm font-bold text-text">AI Setup Guide</span>
                <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active
                </span>
              </div>

              {/* AI opens with a welcome */}
              <div className="flex gap-3 mb-4">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-1">
                  <Sparkles size={12} />
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-text flex-1">
                  <p className="font-semibold mb-3">Welcome to PropertyDirect! Let's set up your society.</p>
                  <div className="space-y-2 mb-4">
                    {[
                      { label: "Society name", value: "Skyline Residency" },
                      { label: "Blocks", value: "3 detected" },
                      { label: "Flats", value: "48 mapped" }
                    ].map((row, i) => (
                      <div key={i} className="flex items-center justify-between bg-white border border-slate-100 rounded-xl px-3.5 py-2">
                        <div className="flex items-center gap-2">
                          <Check size={12} className="text-success" strokeWidth={3} />
                          <span className="text-[12px] text-muted">{row.label}</span>
                        </div>
                        <span className="text-[12px] font-bold text-text">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[13px] text-muted mb-3">Would you like to import your payment history from Excel?</p>
                  <button className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primaryHover transition-colors">
                    <Upload size={12} /> Upload Excel File
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="max-w-[75%] bg-primary text-white text-sm font-medium px-4 py-3 rounded-2xl rounded-tr-sm shadow-sm">
                  Yes, import from Excel
                </div>
              </div>
            </div>
          </div>

          <div className="reveal mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 p-8 bg-white/60 backdrop-blur-sm border border-white/80 rounded-3xl">
            <div>
              <p className="font-bold text-text mb-1">Ready to experience PD Orbit?</p>
              <p className="text-sm text-muted">Ask it anything — from first setup to daily society operations.</p>
            </div>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('pd-orbit:open'))}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 text-primary font-bold text-sm rounded-xl border border-primary/20 hover:bg-primary/15 transition-colors whitespace-nowrap shrink-0"
            >
              <Sparkles size={14} /> Try PD Orbit
            </button>
          </div>
        </div>
      </section>

      {/* OCR Scan Section */}
      <section id="scan" className="py-32 px-[5%] bg-white/40 border-y border-white/50 overflow-hidden relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div className="reveal bg-text rounded-[32px] p-12 relative shadow-2xl">
            <div className="absolute inset-0 bg-dot-grid opacity-[0.1] pointer-events-none"></div>
            <div className="relative z-10 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
              <div className="border-2 border-dashed border-primary/30 rounded-2xl p-10 flex flex-col items-center gap-4 mb-8 bg-black/20">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center text-primary">
                  <Camera size={32} />
                </div>
                <div className="text-xs font-medium text-slate-500">Scanning Utility Bill...</div>
              </div>
              <div className="bg-white/5 rounded-xl p-6 space-y-4 border border-white/10">
                <div className="flex justify-between items-center text-sm text-slate-400"><span>Type</span><span className="text-white font-bold">Electricity Bill</span></div>
                <div className="flex justify-between items-center text-sm text-slate-400"><span>Amount</span><span className="text-white font-bold">₹8,450.00</span></div>
                <div className="h-px bg-white/10"></div>
                <div className="flex justify-center items-center gap-2 text-primary font-bold text-xs ">
                  <span className="w-2 h-2 bg-success rounded-full"></span>
                  OCR Extraction Successful
                </div>
              </div>
            </div>
          </div>

          <div className="reveal">
            <div className="text-primary text-xs font-bold mb-4">AI OCR Scanner</div>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-8">Scan bills on mobile, <br/> sync to web instantly</h3>
            <p className="text-lg text-muted mb-10 leading-relaxed">
              Equip your staff with our OCR scanner. Simply snap a photo of any utility or vendor bill. Our AI extracts the details and creates a payment entry in your portal automatically.
            </p>
            <div className="space-y-6">
              {[
                { title: "AI Extraction", desc: "No manual data entry. OCR reads amounts, dates, and categories." },
                { title: "Real-time Sync", desc: "Bills appear in the resident portal seconds after scanning." },
                { title: "Digital Archive", desc: "Maintain a searchable history of every scanned document." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-success/10 text-success flex items-center justify-center shrink-0 mt-1">
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <div>
                    <h5 className="font-bold text-text">{item.title}</h5>
                    <p className="text-sm text-muted">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-[5%] bg-white/50 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-sm font-bold text-primary  mb-4">Core Platform</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-6">Everything you need for an intelligent society</h3>
            <p className="text-lg text-muted">A complete suite of tools designed specifically for apartment managers and residents.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <CreditCard size={22} />,
                title: "Seamless Maintenance Collection",
                desc: "Collect dues via UPI, cards, or net banking. Money flows directly to your society's bank account with 0% platform commission.",
                tag: "Finance",
                aiAssisted: true
              },
              {
                icon: <MessageSquare size={22} />,
                title: "Automated WhatsApp Alerts",
                desc: "Send payment reminders, receipts, and society notices directly on WhatsApp. 3x higher response rate than email or SMS.",
                tag: "Communication"
              },
              {
                icon: <FileText size={22} />,
                title: "Smart Billing Engine",
                desc: "Generate monthly bills for all flats in seconds. Automatically calculate overdue interest, late fees, and water consumption charges.",
                tag: "Automation",
                aiAssisted: true
              },
              {
                icon: <Sparkles size={22} />,
                title: "AI Society Copilot",
                desc: "Ask questions about your finances, outstanding dues, or billing history in plain English and get instant answers — no reports, no digging.",
                tag: "AI",
                featured: true
              },
              {
                icon: <Users size={22} />,
                title: "Resident Self-Service Portal",
                desc: "Residents log in to view their ledger, download receipts, check dues, and pay online — cutting admin queries by up to 80%.",
                tag: "Residents"
              },
              {
                icon: <Receipt size={22} />,
                title: "Expense & Vendor Tracking",
                desc: "Log society expenditures, attach scanned vendor bills, and track spending by category. Full visibility into where every rupee goes.",
                tag: "Finance"
              }
            ].map((f, i) => (
              <div key={i} className={`reveal group flex flex-col p-8 rounded-2xl bg-white/70 backdrop-blur-sm border transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_-26px_rgba(34,67,140,0.45)] ${f.featured ? 'border-primary/40 ring-1 ring-primary/15' : 'border-white/80'}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-primary/10 text-primary transition-transform duration-500 group-hover:scale-105">
                    {f.icon}
                  </div>
                  {f.aiAssisted && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-primary/8 text-primary border border-primary/15">
                      <Sparkles size={9} /> AI Assisted
                    </span>
                  )}
                </div>
                <h4 className="text-xl font-bold text-text mb-3 tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>{f.title}</h4>
                <p className="text-muted text-[15px] leading-relaxed mb-7" style={{ fontFamily: "'Manrope', sans-serif" }}>{f.desc}</p>
                <div className="mt-auto">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide bg-primary/[0.08] text-primary border border-primary/15">
                    {f.tag}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built For Section */}
      <section className="py-20 px-[5%] bg-white/55 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="reveal text-center mb-12">
            <h2 className="text-sm font-bold text-primary mb-3">Who Is PropertyDirect For?</h2>
            <p className="text-lg font-semibold text-text">Built for every kind of residential community</p>
          </div>
          <div className="reveal flex flex-wrap justify-center gap-4">
            {[
              { icon: <Globe size={16} />, label: "Apartment Communities" },
              { icon: <ShieldCheck size={16} />, label: "Gated Communities" },
              { icon: <Users size={16} />, label: "Residential Welfare Associations" },
              { icon: <FileText size={16} />, label: "Builders & Developers" },
              { icon: <Receipt size={16} />, label: "Property Managers" }
            ].map((item) => (
              <div key={item.label} className="inline-flex items-center gap-2.5 px-5 py-3 bg-white/80 border border-white/80 rounded-2xl backdrop-blur-sm shadow-sm hover:border-primary/20 hover:bg-primary/3 transition-all duration-200">
                <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <span className="text-sm font-semibold text-text">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Migration Section */}
      <section id="migration" className="py-32 px-[5%] bg-white/40 border-y border-white/50 overflow-hidden relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Dark mockup panel */}
          <div className="reveal bg-text rounded-[32px] p-12 relative shadow-2xl order-1 lg:order-none">
            <div className="absolute inset-0 bg-dot-grid opacity-[0.1] pointer-events-none rounded-[32px]"></div>
            <div className="relative z-10 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8">

              {/* Source system badges */}
              <div className="mb-6 pb-5 border-b border-white/10">
                <div className="text-slate-500 text-[10px] font-bold mb-2">Works with your existing files</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { label: "Excel (.xlsx)", icon: "📊" },
                    { label: "Excel (.xls)", icon: "📊" },
                    { label: "CSV", icon: "📄" }
                  ].map((src) => (
                    <span key={src.label} className="px-2.5 py-1 bg-white/10 border border-white/15 rounded-full text-[11px] font-semibold text-slate-300 flex items-center gap-1">
                      <Check size={9} className="text-emerald-400" strokeWidth={3} /> {src.label}
                    </span>
                  ))}
                </div>
                <p className="text-slate-600 text-[10px]">Cell colours, merged cells and formatting don't affect data extraction.</p>
              </div>

              {/* AI pipeline steps */}
              <div className="relative pl-5 mb-6">
                <div className="absolute left-[9px] top-3 bottom-3 w-px bg-white/10" />
                {[
                  { label: "Upload your spreadsheet", final: false },
                  { label: "AI detects structure automatically", final: false },
                  { label: "Maps flats, residents & balances", final: false },
                  { label: "Flags anything that needs your review", final: false, warn: true },
                  { label: "Import confirmed — data is live", final: true }
                ].map((s, i) => (
                  <div key={i} className={`flex items-center gap-3 py-2.5 px-3 rounded-xl mb-1 relative ${s.final ? 'bg-emerald-500/10 border border-emerald-500/15' : s.warn ? 'bg-amber-500/8 border border-amber-500/10' : ''}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 relative z-10 ${s.final ? 'bg-emerald-500' : s.warn ? 'bg-amber-500/30 border border-amber-500/20' : 'bg-primary/30 border border-primary/20'}`}>
                      <Check size={9} className={s.final ? 'text-white' : s.warn ? 'text-amber-400/70' : 'text-primary/70'} strokeWidth={3} />
                    </div>
                    <span className={`text-[13px] font-medium ${s.final ? 'text-emerald-400 font-bold' : s.warn ? 'text-amber-300' : 'text-slate-400'}`}>{s.label}</span>
                    {s.final && (
                      <span className="ml-auto flex items-center gap-1 text-[10px] font-bold text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Done
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Import summary mockup */}
              <div className="bg-white/5 border border-white/8 rounded-xl p-4 mb-4">
                <div className="text-slate-400 text-[10px] font-bold mb-3">Import Summary</div>
                <div className="space-y-2">
                  {[
                    { label: "127 Flats", ok: true },
                    { label: "94 Residents", ok: true },
                    { label: "3 Blocks", ok: true },
                    { label: "₹4.3L Outstanding", ok: true },
                    { label: "2 Rows Need Attention", ok: false }
                  ].map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${row.ok ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                        {row.ok
                          ? <Check size={8} className="text-emerald-400" strokeWidth={3} />
                          : <span className="text-[8px] text-amber-400 font-black leading-none">!</span>
                        }
                      </div>
                      <span className={`text-[12px] font-medium ${row.ok ? 'text-slate-300' : 'text-amber-300'}`}>{row.label}</span>
                      {!row.ok && <span className="ml-auto text-[10px] font-bold text-amber-400 underline cursor-pointer">Review Now</span>}
                    </div>
                  ))}
                </div>
              </div>

              <button className="w-full py-3.5 rounded-xl bg-primary text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-primaryHover transition-colors shadow-[0_10px_28px_-10px_rgba(79,124,255,0.6)]">
                <Upload size={15} /> Confirm Import
              </button>
            </div>
          </div>

          {/* Text side */}
          <div className="reveal">
            <div className="text-primary text-xs font-bold mb-4">Zero Data Loss Migration</div>
            <h3 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-6" style={{ fontFamily: "'Sora', sans-serif" }}>
              Import your existing records.<br />In minutes, not days.
            </h3>

            {/* Reassurance strip */}
            <div className="flex items-start gap-3 px-5 py-4 bg-emerald-50 border border-emerald-200/70 rounded-2xl mb-8">
              <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-emerald-800">AI validates your data and highlights anything that needs your attention before a single record is imported.</p>
            </div>

            <p className="text-lg text-muted mb-8 leading-relaxed">
              Upload your spreadsheets as-is — any layout, any formatting. Our AI automatically detects your data structure and maps it to PropertyDirect. If it can't confidently map a field, it flags it for your review before committing.
            </p>

            {/* What gets migrated */}
            <div className="mb-8">
              <p className="text-xs font-bold text-text mb-4">What gets migrated</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "Flats & Units",
                  "Residents",
                  "Opening Balances",
                  "Outstanding Dues",
                  "Electricity Bills",
                  "Water Bills & Readings",
                  "Maintenance Bills",
                  "Expenditure Records"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm font-medium text-text/80">
                    <Check size={13} className="text-success shrink-0" strokeWidth={3} />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Trust line */}
            <p className="text-sm text-muted mb-6 leading-relaxed">Nothing is imported until you've had a chance to review detected issues. Existing records are never overwritten without validation.</p>

            {/* Format callout */}
            <div className="flex items-start gap-3 px-5 py-4 bg-slate-50 border border-slate-200/70 rounded-2xl mb-6">
              <FileText size={15} className="text-slate-500 shrink-0 mt-0.5" />
              <p className="text-sm text-text/70">In most cases, no reformatting is required. If something can't be mapped confidently, we'll highlight it before import.</p>
            </div>

            {/* Before → After table */}
            <div className="mb-8 rounded-2xl border border-slate-100 overflow-hidden text-sm">
              <div className="grid grid-cols-2">
                <div className="px-4 py-2.5 bg-slate-50 font-bold text-text/60 text-[11px]">Today</div>
                <div className="px-4 py-2.5 bg-primary/5 font-bold text-primary text-[11px]">With PropertyDirect</div>
              </div>
              {[
                ["Multiple Excel files", "Unified dashboard"],
                ["Manual calculations", "Automated billing"],
                ["Scattered records", "Centralised ledger"],
                ["Spreadsheet tracking", "Real-time AI insights"]
              ].map(([before, after], i) => (
                <div key={i} className={`grid grid-cols-2 border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                  <div className="px-4 py-3 text-muted text-[13px]">{before}</div>
                  <div className="px-4 py-3 text-text font-medium text-[13px] flex items-center gap-1.5">
                    <Check size={11} className="text-success shrink-0" strokeWidth={3} /> {after}
                  </div>
                </div>
              ))}
            </div>

            {/* Migration support note */}
            <div className="flex items-start gap-3 px-5 py-4 bg-primary/5 border border-primary/15 rounded-2xl">
              <Zap size={15} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-text/80">Our onboarding team will assist with your migration at no additional cost — so you're never on your own.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section — uses only the figure already established in the page; no invented metrics */}
      <section className="py-20 px-[5%] bg-white/60 relative z-10">
        <div className="reveal max-w-4xl mx-auto text-center">
          <span className="text-5xl md:text-6xl font-bold text-primary block mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>40+</span>
          <p className="text-lg font-semibold text-text/80 mb-2">Societies already running on PropertyDirect</p>
          <p className="text-sm text-muted">Real communities, real collections, real automation — every day.</p>
        </div>
      </section>

      {/* Testimonials Section — honest placeholder until real customer quotes are collected */}
      <section id="testimonials" className="py-28 px-[5%] bg-white/45 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="reveal text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-sm font-bold text-primary mb-4">What Societies Say</h2>
            <h3 className="text-4xl font-bold text-text tracking-tight mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
              We're collecting our first customer stories
            </h3>
            <p className="text-muted">Real testimonials from societies using PropertyDirect will appear here soon.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="reveal flex flex-col justify-between p-7 rounded-2xl border border-dashed border-slate-300 bg-white/40 min-h-[180px]">
                <p className="text-sm text-muted italic">"Testimonial coming soon."</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-200" />
                  <div>
                    <div className="h-2.5 w-24 bg-slate-200 rounded-full mb-1.5" />
                    <div className="h-2 w-16 bg-slate-100 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-32 px-[5%] bg-white/40 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="reveal text-center mb-20">
            <h2 className="text-4xl font-bold text-text tracking-tight mb-4">Frequently Asked Questions</h2>
            <p className="text-muted">Everything you need to know about PropertyDirect.</p>
          </div>
          <div className="space-y-4">
            {[
              { q: "Is the setup complex?", a: "Not at all. We handle the data migration and setup for you. Most societies go live within 24 hours of sharing their flat records." },
              // TODO(business/payments): settlement-flow claim below can't be verified from application code alone — confirm with the payments team before production release.
              { q: "Is my society's money safe?", a: "Yes. PropertyDirect is not a wallet. All maintenance payments go directly to your society's registered bank account via the payment gateway. We never hold your funds." },
              { q: "Are my payment details safe?", a: "Yes. PropertyDirect never stores your debit or credit card details. Payments are processed through our secure, encrypted payment gateway — your card information never touches our servers." },
              { q: "Can committee members access financial data?", a: "Access is role-based. Only authorized admins and committee members with the right permissions can view sensitive financial information — residents and other roles see only what's relevant to them." },
              { q: "Do residents need to download an app?", a: "No app required for residents. They get alerts and payment links via WhatsApp, pay in one click, and receive receipts instantly." },
              { q: "We manage records in Excel. Can we migrate our data?", a: "Yes. Our onboarding team handles the migration for you. Share your flat list, owner details, and outstanding dues in any format — Excel, CSV, or even a WhatsApp message — and we'll import it before you go live." },
              { q: "Can multiple admins or committee members access the portal?", a: "Yes. You can add multiple admins with different roles — Accountant, Auditor, Society Manager, or Committee Member — each with appropriate access. No limit on the number of admins on paid plans." },
              { q: "Can I upgrade or downgrade my plan anytime?", a: "Yes. You can change your plan at any time from the subscription settings. Upgrades take effect immediately. There are no lock-in periods or cancellation fees." },
              { q: "What support do you provide?", a: "We provide a dedicated WhatsApp support group for admins, guided onboarding, and help migrating your existing data. Most issues are resolved within a few hours." }
            ].map((item, i) => (
              <div key={i} className="reveal bg-white/70 backdrop-blur-sm border border-white/80 rounded-2xl overflow-hidden group">
                <button
                  onClick={toggleFaq}
                  className="w-full px-7 py-5 flex items-center justify-between text-left group-hover:bg-white/50 transition-colors"
                >
                  <span className="font-bold text-text">{item.q}</span>
                  <span className="faq-arrow text-muted transition-transform">+</span>
                </button>
                <div className="faq-a px-8 text-muted text-sm leading-relaxed">
                  {item.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-32 px-[5%] bg-white/40 relative z-10 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 -skew-x-12 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 pointer-events-none" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-[10px] font-bold  mb-6">
                <Zap size={12} /> Get Started Today
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-text tracking-tight mb-8 leading-[1.1]">
                Start managing your society <span className="text-primary italic">today</span>
              </h2>
              <p className="text-lg text-muted mb-10 max-w-lg leading-relaxed">
                Takes less than 10 minutes, no credit card required. Join 40+ premium, forward-thinking societies already using PropertyDirect to automate their finances and communication.
              </p>

              <div className="space-y-4">
                {[
                  "24-Hour Express Onboarding",
                  "Dedicated Support WhatsApp Group",
                  "Zero Upfront Implementation Cost"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-5 w-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                      <CheckCircle2 size={12} strokeWidth={3} />
                    </div>
                    <span className="text-sm font-bold text-text/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-[0_30px_60px_-30px_rgba(34,67,140,0.35)] border border-white/80">
              <h3 className="text-xl font-bold text-text mb-8">Request a Free Demo</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {success && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold rounded-2xl flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                    <span>Demo request received! Our team will contact you within 2 hours.</span>
                  </div>
                )}
                {error && (
                  <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 text-sm font-bold rounded-2xl flex items-center gap-3">
                    <ShieldCheck size={18} className="text-rose-500 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 ml-1">Full Name</label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={loading}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all placeholder:text-slate-400"
                      placeholder="e.g. Rahul Gupta"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 ml-1">Mobile Number</label>
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={loading}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all placeholder:text-slate-400"
                      placeholder="+91 00000 00000"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500 ml-1">Society Name & Location</label>
                  <input
                    type="text"
                    required
                    value={societyName}
                    onChange={(e) => setSocietyName(e.target.value)}
                    disabled={loading}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all placeholder:text-slate-400"
                    placeholder="e.g. Skyline Residency, Mumbai"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-5 rounded-2xl"
                >
                  {loading ? "Scheduling Demo..." : "Secure My Free Slot"}
                </button>
                <p className="text-center text-[10px] text-muted font-medium">
                  Your information is used only to contact you about your demo. No spam, no credit card required — we never store your debit or credit card details.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-white/60 border-t border-white/50 pt-24 pb-12 px-[5%]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 mb-20">
            {/* Brand Section */}
            <div className="md:col-span-4 space-y-6">
              <LogoFull light={false} size="md" />
              <p className="text-sm text-muted leading-relaxed max-w-sm">
                PropertyDirect is the premier next-generation ERP for residential communities,
                designed to bring transparency, automation, and a superior resident experience to society management.
              </p>
              <div className="flex items-center gap-3 py-2 px-4 bg-slate-50 rounded-2xl w-fit border border-slate-100">
                <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-[10px] font-bold text-slate-500 ">System Operational</span>
              </div>
            </div>

            {/* Links Sections */}
            <div className="md:col-span-2 space-y-6 md:pt-[1px]">
              <h5 className="text-[11px] font-bold text-text ">Product</h5>
              <ul className="space-y-4 text-sm text-muted">
                <li><a href="#features" className="hover:text-primary transition-colors">Platform Features</a></li>
                <li><a href="#scan" className="hover:text-primary transition-colors">Smart Billing</a></li>
                <li><a href="#pricing" className="hover:text-primary transition-colors">Pricing Plans</a></li>
              </ul>
            </div>

            <div className="md:col-span-2 space-y-6 md:pt-[1px]">
              <h5 className="text-[11px] font-bold text-text ">Company</h5>
              <ul className="space-y-4 text-sm text-muted">
                <li><a href="#faq" className="hover:text-primary transition-colors">Help Center</a></li>
                <li><a href="#contact" className="hover:text-primary transition-colors">Contact Support</a></li>
                <li><Link to="/auth" className="hover:text-primary transition-colors">Partner Login</Link></li>
              </ul>
            </div>

            {/* Contact Section */}
            <div className="md:col-span-4 space-y-6 md:pt-[1px]">
              <h5 className="text-[11px] font-bold text-text ">Get in Touch</h5>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-softBlue rounded-xl flex items-center justify-center text-primary">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-muted">Email Inquiry</p>
                    <a href="mailto:support@propertydirect.in" className="text-sm font-bold text-text hover:text-primary transition-colors">support@propertydirect.in</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-4 text-xs text-muted">
              <span>© 2026 PropertyDirect. All rights reserved.</span>
              <span className="hidden md:block h-1 w-1 rounded-full bg-slate-300"></span>
              <div className="flex gap-6">
                <a href="#" className="hover:text-primary transition-colors">Privacy</a>
                <a href="#" className="hover:text-primary transition-colors">Terms</a>
              </div>
            </div>

            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 ">Built with</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#F43F5E"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.78-8.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              <span className="text-[10px] font-bold text-slate-400 ">for Indian Societies</span>
            </div>
          </div>
        </div>
      </footer>
      <AICopilot />
    </div>
  );
};

export default LandingPage;
